import { log, Source, error } from "./logger";
import { CancelablePromise } from "./cancelablePromise";
import { dispatcher, DispatcherEvents } from "./dispatcher";
import { getDatabaseNameFromPath } from "./uri";
import { tryUpdateReload } from "./updateReloadHelper";

export function unregisterServiceWorkers(database?: string, condition = (dbName?: string, databaseName?: string) => !!dbName && dbName !== "drive" && dbName !== databaseName) {
    if (!("serviceWorker" in navigator)) {
        return CancelablePromise.resolve();
    }

    let databasesToClear: string[] = [];
    return navigator.serviceWorker.getRegistrations()
        .then((registrations) => {
            let registrationPromises: CancelablePromise<boolean>[] = [];
            for (let registration of registrations) {
                let registrationUrl = (registration.active?.scriptURL || "").toLowerCase();
                // drive/build/service-worker is used by release 9.0 and above, drive/service-worker is used by release 8.0 and below
                if (registrationUrl.indexOf("drive/build/service-worker") > -1 || registrationUrl.indexOf("drive/service-worker") > -1) {
                    let path = registration.scope.replace(window.location.origin, "").split("/");
                    let dbName = path.length ? path[1] && path[1].toLowerCase() : "";
                    if (condition(dbName, database)) {
                        // only unregister other sw
                        registrationPromises.push(registration.unregister() as CancelablePromise);
                        databasesToClear.push(dbName === "drive" ? "[*]" : `[${ dbName }]`);
                    }
                }
            }
            return CancelablePromise.all(registrationPromises);
        })
        .then(() => caches.keys())
        .then(keys => {
            let i: number;
            let key: string;
            let cacheRemovePromises: CancelablePromise<boolean>[] = [];
            for (let staleDatabase of databasesToClear) {
                for (i = 0; i < keys.length; i++) {
                    key = keys[i];
                    if (key.toLowerCase().indexOf(staleDatabase.toLowerCase()) > -1) {
                        cacheRemovePromises.push(caches.delete(key) as CancelablePromise<boolean>);
                        log(Source.SERVWORKER, `Deleting cache key. key=${ key }`);
                        break;
                    }
                }
            }
            return CancelablePromise.all(cacheRemovePromises);
        });
}

function rejectServiceWorkerPromise(reason: string) {
    return new CancelablePromise<ServiceWorkerRegistration>((_, reject) => reject(reason));
}

function skipWaiting(reg: ServiceWorkerRegistration) {
    if (reg.waiting) {
        // Service worker is waiting
        reg.waiting.postMessage({type: "activate-skip-waiting"} as IServiceWorkerPostMessage);
    }
}

export type TServiceWorkerStatus = ServiceWorkerState | "active" | "unknown";

export class DriveServiceWorker {
    private swRegistrationPromise: CancelablePromise<ServiceWorkerRegistration>;
    private swRegistration: ServiceWorkerRegistration;
    private swCurrentState: TServiceWorkerStatus;

    get state() {
        return this.swCurrentState || "unknown";
    }

    private get registration() {
        return this.swRegistration ?
            CancelablePromise.resolve<ServiceWorkerRegistration>(this.swRegistration) : (this.swRegistrationPromise || this.init().then(() => this.swRegistration));
    }

    init(serviceWorkerRegistrationPromise = navigator.serviceWorker.register("build/service-worker.pack.js", {scope: "."})) {
        if (!window.serviceWorkerEnabled) {
            return rejectServiceWorkerPromise("Service worker not enabled");
        }
        if ("serviceWorker" in navigator) {
            log(Source.SERVWORKER, "Registering SW message listener");
            navigator.serviceWorker.addEventListener("message", evt => {
                log(Source.SERVWORKER, `SW message received: ${ JSON.stringify(evt.data) }`);
                if (!evt.data) {
                    log(Source.SERVWORKER, "SW message ignored: evt.data is falsy");
                    return;
                }
                let data = evt.data as IServiceWorkerPostMessage;
                if (!data.type) {
                    log(Source.SERVWORKER, `SW message ignored: data.type is falsy, data=${ JSON.stringify(data) }`);
                    return;
                }
                log(Source.SERVWORKER, `SW message processing: type=${ data.type }`);
                switch (data.type) {
                    case "activate-do-reload":
                        // When the SW updates and needs to ensure all assets are up-to-date
                        // Only way to do that is a full page reload
                        log(Source.SERVWORKER, `activate-do-reload DISABLE_SERVICEWORKER_RELOAD=${ window.DRIVE_DEBUG?.DISABLE_SERVICEWORKER_RELOAD }`);
                        if (!window.DRIVE_DEBUG?.DISABLE_SERVICEWORKER_RELOAD) {
                            tryUpdateReload();
                        }
                        break;
                    default:
                        break;
                }
            });
            this.swRegistrationPromise = new CancelablePromise<ServiceWorkerRegistration>((resolve, reject) => {
                return this.unregisterOldServiceWorker()
                    .then(() => serviceWorkerRegistrationPromise, err => {
                        error(Source.SERVWORKER, `Unregister service worker failed: ${ err }`);
                        return serviceWorkerRegistrationPromise;
                    })
                    .then(resolve, err => {
                        error(Source.SERVWORKER, `Service worker registration failed: ${ err }`);
                        reject(err);
                    });
            })
            .then(reg => {
                log(Source.SERVWORKER, `SW registered: scope=${ reg.scope }, active=${ !!reg.active }, waiting=${ !!reg.waiting }, installing=${ !!reg.installing }`);
                if (reg.active) {
                    this.swCurrentState = "active";
                }
                reg.onupdatefound = this.onUpdateFound(reg);
                dispatcher.trigger(DispatcherEvents.ServiceWorkerRegistered);
                log(Source.SERVWORKER, "Successfully registered");
                this.swRegistration = reg;
                return reg;
            });
            return this.swRegistrationPromise;
        }
        let serviceWorkerNotSupportedError = "Service worker not supported";
        error(Source.SERVWORKER, serviceWorkerNotSupportedError);
        return rejectServiceWorkerPromise(serviceWorkerNotSupportedError);
    }

    update() {
        if (!window.serviceWorkerEnabled) {
            return rejectServiceWorkerPromise("Service worker not enabled");
        }
        let reg = this.registration;
        return reg
            .then(r => r.update())
            .then(() => reg);
    }

    skipWaiting() {
        if (!window.serviceWorkerEnabled) {
            return rejectServiceWorkerPromise("Service worker not enabled");
        }
        let reg = this.registration;
        return reg
            .then(skipWaiting)
            .then(() => reg);
    }

    private unregisterOldServiceWorker() {
        let databaseName = getDatabaseNameFromPath();
        if (!databaseName) {
            return CancelablePromise.resolve();
        }

        return unregisterServiceWorkers(databaseName);
    }

    private onUpdateFound(reg: ServiceWorkerRegistration) {
        let onInstalling = () => {
            window.performance?.mark && window.performance.mark("ServiceWorkerUpdateInstallingStart");
            log(Source.SERVWORKER, "Installing new version...");
            dispatcher.trigger(DispatcherEvents.ServiceWorkerUpdateInstalling);
        };

        return () => {
            log(Source.SERVWORKER, `onUpdateFound triggered: installing=${ !!reg.installing }, waiting=${ !!reg.waiting }`);
            let newWorker = reg.installing || reg.waiting;
            if (!newWorker) {
                log(Source.SERVWORKER, "onUpdateFound: no newWorker found, returning early");
                return;
            }
            dispatcher.trigger(DispatcherEvents.ServiceWorkerUpdateFound);
            this.swCurrentState = newWorker.state;
            onInstalling();
            newWorker.onstatechange = () => {
                log(Source.SERVWORKER, `SW state changed: ${ newWorker?.state }`);
                if (!newWorker?.state) {
                    log(Source.SERVWORKER, "SW state change ignored: state is falsy");
                    return;
                }
                this.swCurrentState = newWorker.state;
                switch (newWorker.state) {
                    case "installing":
                        onInstalling();
                        break;
                    case "activated":
                        dispatcher.trigger(DispatcherEvents.ServiceWorkerUpdateActivated);
                        log(Source.SERVWORKER, "Activated");
                        break;
                    case "installed":
                        if (navigator.serviceWorker.controller) {
                            // Updated content has been fetched, but old service worker will serve content until all
                            // client tabs are closed
                            log(Source.SERVWORKER, "Installed new version");
                            window.performance?.measure && window.performance.measure("ServiceWorkerUpdate", "ServiceWorkerUpdateInstallingStart");
                            dispatcher.trigger(DispatcherEvents.ServiceWorkerUpdateSuccess);
                        } else {
                            // Everything has been cached and app is ready to be used offline
                            log(Source.SERVWORKER, "Installed successfully");
                            window.performance?.measure && window.performance.measure("ServiceWorkerInstall", "ServiceWorkerUpdateInstallingStart");
                            dispatcher.trigger(DispatcherEvents.ServiceWorkerInstallSuccess);
                        }
                        break;
                    default:
                        break;
                }
            };
        };
    }
}

export default new DriveServiceWorker();
